import { JobClass } from '@breezy/shared'
import tailwindConfig from '../tailwind.config'
import { userColor } from './color-utils'

export type EventColorConfig = {
  ribbon: string
  completedRibbon: string
  background: string
  eventColor: string
  mapPinColor: string
}

const hexToHSL = (hex: string) => {
  hex = hex.replace('#', '')

  const r = parseInt(hex.substring(0, 2), 16) / 255
  const g = parseInt(hex.substring(2, 4), 16) / 255
  const b = parseInt(hex.substring(4, 6), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  let h = 0,
    s = 0

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      case b:
        h = (r - g) / d + 4
        break
    }
    h /= 6
  }

  return { h: h * 360, s: s * 100, l: l * 100 }
}

const hslToHex = (h: number, s: number, l: number) => {
  l /= 100
  const a = (s * Math.min(l, 1 - l)) / 100
  const f = (n: number) => {
    const k = (n + h / 30) % 12
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0')
  }
  return `#${f(0)}${f(8)}${f(4)}`
}

export const toEventColorConfig = (primaryHueHex: string): EventColorConfig => {
  const baseHSL = hexToHSL(primaryHueHex)

  const mainAccent = primaryHueHex

  const lightAccent = hslToHex(baseHSL.h, Math.max(baseHSL.s, 0), Math.min(baseHSL.l + 20, 100))

  const background = hslToHex(baseHSL.h, Math.max(baseHSL.s, 0), Math.min(baseHSL.l + 38, 95))

  return {
    eventColor: background,
    background: background,
    mapPinColor: mainAccent,
    ribbon: mainAccent,
    completedRibbon: lightAccent,
  }
}

export const JOB_CLASS_COLOR_MAP = {
  SERVICE: {
    ribbon: 'bg-bz-cyan-600',
    completedRibbon: 'bg-bz-cyan-400',
    background: 'bg-bz-cyan-200',
    eventColor: tailwindConfig.theme.extend.colors['bz-cyan'][200],
    mapPinColor: tailwindConfig.theme.extend.colors['bz-cyan'][600],
  },
  MAINTENANCE: {
    ribbon: 'bg-geek-blue-600',
    completedRibbon: 'bg-geek-blue-400',
    background: 'bg-geek-blue-200',
    eventColor: tailwindConfig.theme.extend.colors['geek-blue'][200],
    mapPinColor: tailwindConfig.theme.extend.colors['geek-blue'][600],
  },
  INSTALL: {
    ribbon: 'bg-bz-magenta-600',
    completedRibbon: 'bg-bz-magenta-400',
    background: 'bg-bz-magenta-200',
    eventColor: tailwindConfig.theme.extend.colors['bz-magenta'][200],
    mapPinColor: tailwindConfig.theme.extend.colors['bz-magenta'][600],
  },
  SALES: {
    ribbon: 'bg-bz-volcano-600',
    completedRibbon: 'bg-bz-volcano-400',
    background: 'bg-bz-volcano-200',
    eventColor: tailwindConfig.theme.extend.colors['bz-volcano'][200],
    mapPinColor: tailwindConfig.theme.extend.colors['bz-volcano'][600],
  },
} satisfies Partial<Record<JobClass, EventColorConfig>>

export const DEFAULT_EVENT_COLOR_CONFIG = {
  ribbon: 'bg-[#8c8c8c]',
  completedRibbon: 'bg-bz-text-quaternary',
  background: 'bg-[#d9d9d9]',
  eventColor: '#d9d9d9',
  mapPinColor: '#d9d9d9',
} satisfies EventColorConfig

export const getColorForJobClass = (jobClass: JobClass) =>
  JOB_CLASS_COLOR_MAP[jobClass as keyof typeof JOB_CLASS_COLOR_MAP] ?? DEFAULT_EVENT_COLOR_CONFIG

export const getColorForJobType = (jobType: { color?: string | null; jobTypeGuid: string }): EventColorConfig =>
  toEventColorConfig(jobType.color ?? userColor(jobType.jobTypeGuid))
